/**
 * DebugPanel.tsx — Admin Debug Panel for Adroit
 * Shows raw SQL queries behind every API call.
 * Only visible to admin/super_admin. Zero footprint when off.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import apiClient from '../../api/client';
import legacyApi from '../../services/api';
import { safeRandomUUID } from '../../utils/safeRandomUUID';

// ── Types ──

export interface DebugEntry {
  id: string;
  timestamp: string;
  endpoint: string;
  method: string;
  params: Record<string, any>;
  sql: string | null;
  tables: string[];
  responseTimeMs: number;
  statusCode: number;
  error: string | null;
  stackTrace: string | null;
}

interface DebugPanelProps {
  userRole: 'super_admin' | 'admin' | 'member' | 'trial_member' | 'guest';
}

// ── Debug Store (in-memory singleton) ──

type Listener = () => void;

class DebugStore {
  private entries: DebugEntry[] = [];
  private listeners: Set<Listener> = new Set();
  private _active = false;

  get active() { return this._active; }
  activate() { this._active = true; this.notify(); }
  deactivate() { this._active = false; this.notify(); }

  push(entry: DebugEntry) {
    this.entries.unshift(entry);
    if (this.entries.length > 200) this.entries = this.entries.slice(0, 200);
    this.notify();
  }

  getEntries(): DebugEntry[] { return this.entries; }
  clear() { this.entries = []; this.notify(); }
  subscribe(listener: Listener) {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }
  private notify() { this.listeners.forEach(l => l()); }
}

const debugStore = new DebugStore();

// ── Axios Interceptor ──
// The app uses axios (not window.fetch) for all API calls.
// We install interceptors on both axios instances used in the app.

let interceptorInstalled = false;

function buildEndpoint(config: InternalAxiosRequestConfig): string {
  const base = config.baseURL || '';
  const url = config.url || '';
  // Combine baseURL + url, strip origin if present
  const full = url.startsWith('http') ? url : base + url;
  return full.replace(/^https?:\/\/[^/]+/, '');
}

function extractParams(config: InternalAxiosRequestConfig): Record<string, any> {
  const params: Record<string, any> = {};
  // Query params
  if (config.params) {
    Object.entries(config.params).forEach(([k, v]) => { params[k] = v; });
  }
  // Request body
  if (config.data) {
    try {
      const body = typeof config.data === 'string' ? JSON.parse(config.data) : config.data;
      if (typeof body === 'object' && body !== null && !(body instanceof FormData)) {
        Object.assign(params, body);
      }
    } catch { /* non-JSON body, skip */ }
  }
  return params;
}

function installOnInstance(instance: AxiosInstance) {
  // Request interceptor: add X-Debug header + timestamp
  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    if (debugStore.active) {
      config.headers['X-Debug'] = 'true';
      (config as any)._debugStartTime = performance.now();
    }
    return config;
  });

  // Response interceptor: capture debug entries
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      if (!debugStore.active) return response;

      const startTime = (response.config as any)._debugStartTime;
      if (startTime == null) return response;
      const elapsed = Math.round(performance.now() - startTime);
      const endpoint = buildEndpoint(response.config);
      const method = (response.config.method || 'GET').toUpperCase();
      const params = extractParams(response.config);
      const debug = response.data?._debug;

      if (debug) {
        const queries: any[] = Array.isArray(debug.queries) ? debug.queries : [{ sql: 'No SQL captured' }];
        for (const q of queries) {
          debugStore.push({
            id: safeRandomUUID(),
            timestamp: new Date().toISOString(),
            endpoint, method, params,
            sql: q.sql || null,
            tables: q.tables || [],
            responseTimeMs: q.duration_ms ?? elapsed,
            statusCode: response.status,
            error: q.error || null,
            stackTrace: q.stack_trace || null,
          });
        }
      } else {
        debugStore.push({
          id: safeRandomUUID(),
          timestamp: new Date().toISOString(),
          endpoint, method, params,
          sql: null, tables: [],
          responseTimeMs: elapsed,
          statusCode: response.status,
          error: null,
          stackTrace: null,
        });
      }

      return response;
    },
    (error: AxiosError) => {
      if (debugStore.active && error.config) {
        const startTime = (error.config as any)._debugStartTime;
        const elapsed = startTime != null ? Math.round(performance.now() - startTime) : 0;
        debugStore.push({
          id: safeRandomUUID(),
          timestamp: new Date().toISOString(),
          endpoint: buildEndpoint(error.config as InternalAxiosRequestConfig),
          method: (error.config.method || 'GET').toUpperCase(),
          params: extractParams(error.config as InternalAxiosRequestConfig),
          sql: null, tables: [],
          responseTimeMs: elapsed,
          statusCode: error.response?.status || 0,
          error: error.response ? `HTTP ${error.response.status}` : (error.message || 'Network error'),
          stackTrace: null,
        });
      }
      return Promise.reject(error);
    }
  );
}

function installDebugInterceptor() {
  if (interceptorInstalled) return;
  interceptorInstalled = true;

  installOnInstance(apiClient);   // src/api/client.ts — primary
  installOnInstance(legacyApi);   // src/services/api.ts — legacy
}

// ── Theme (matches Adroit dark theme) ──

const C = {
  bg: '#0d1117', bgPanel: '#161b22', bgEntry: '#1c2129', bgEntryHover: '#21262d',
  border: '#30363d', borderLight: '#21262d',
  text: '#c9d1d9', textMuted: '#8b949e', textDim: '#484f58',
  accent: '#58a6ff', green: '#3fb950', yellow: '#d29922', red: '#f85149', orange: '#d18616',
  mono: "'JetBrains Mono', 'Fira Code', 'SF Mono', monospace",
  sans: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
};

// ── Sub-components ──

function StatusDot({ code }: { code: number }) {
  const color = code === 0 ? C.red : code < 300 ? C.green : code < 400 ? C.yellow : code < 500 ? C.orange : C.red;
  return <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: color, boxShadow: `0 0 6px ${color}40`, flexShrink: 0 }} />;
}

function TimeBadge({ ms }: { ms: number }) {
  const color = ms < 100 ? C.green : ms < 500 ? C.yellow : C.red;
  return <span style={{ fontFamily: C.mono, fontSize: 11, color, background: `${color}15`, padding: '2px 6px', borderRadius: 3, flexShrink: 0 }}>{ms}ms</span>;
}

function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try { await navigator.clipboard.writeText(text); } catch {
      const ta = document.createElement('textarea'); ta.value = text;
      document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
    }
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={handleCopy} style={{
      background: copied ? `${C.green}20` : `${C.accent}10`,
      border: `1px solid ${copied ? C.green : C.border}`,
      color: copied ? C.green : C.accent,
      padding: '3px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontFamily: C.sans,
    }}>
      {copied ? '\u2713 Copied' : label}
    </button>
  );
}

// ── Debug Entry Row ──

function DebugEntryRow({ entry }: { entry: DebugEntry }) {
  const [expanded, setExpanded] = useState(false);
  const time = new Date(entry.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  const clipboardText = [
    `## Debug Entry \u2014 ${entry.endpoint}`,
    `**Time:** ${entry.timestamp}`,
    `**Method:** ${entry.method}`,
    `**Status:** ${entry.statusCode}`,
    `**Response time:** ${entry.responseTimeMs}ms`,
    entry.tables.length ? `**Tables:** ${entry.tables.join(', ')}` : null,
    Object.keys(entry.params).length ? `**Params:**\n\`\`\`json\n${JSON.stringify(entry.params, null, 2)}\n\`\`\`` : null,
    entry.sql ? `**SQL:**\n\`\`\`sql\n${entry.sql}\n\`\`\`` : null,
    entry.error ? `**Error:** ${entry.error}` : null,
    entry.stackTrace ? `**Stack trace:**\n\`\`\`\n${entry.stackTrace}\n\`\`\`` : null,
  ].filter(Boolean).join('\n\n');

  return (
    <div style={{ borderBottom: `1px solid ${C.borderLight}` }}>
      <div onClick={() => setExpanded(!expanded)} style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
        cursor: 'pointer', background: expanded ? C.bgEntry : 'transparent',
        fontSize: 12, fontFamily: C.mono,
      }}
        onMouseEnter={e => { if (!expanded) (e.currentTarget as HTMLElement).style.background = C.bgEntryHover; }}
        onMouseLeave={e => { if (!expanded) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
      >
        <span style={{ color: C.textDim, fontSize: 10, flexShrink: 0, transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>{'\u25B6'}</span>
        <StatusDot code={entry.statusCode} />
        <span style={{ color: C.textMuted, flexShrink: 0, width: 60 }}>{time}</span>
        <span style={{ color: entry.method === 'GET' ? C.green : entry.method === 'POST' ? C.accent : C.yellow, flexShrink: 0, width: 36, fontSize: 10, fontWeight: 600 }}>{entry.method}</span>
        <span style={{ color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>{entry.endpoint}</span>
        {entry.tables.length > 0 && <span style={{ color: C.textDim, fontSize: 10, flexShrink: 0 }}>{entry.tables.join(', ')}</span>}
        <TimeBadge ms={entry.responseTimeMs} />
        {entry.error && <span style={{ color: C.red, fontSize: 10, flexShrink: 0 }}>ERR</span>}
      </div>

      {expanded && (
        <div style={{ background: C.bgEntry, padding: '0 12px 12px 32px', fontSize: 12, fontFamily: C.mono, lineHeight: 1.6 }}>
          {Object.keys(entry.params).length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <span style={{ color: C.textMuted, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Parameters</span>
              <pre style={{ background: C.bg, border: `1px solid ${C.borderLight}`, borderRadius: 4, padding: 8, margin: '4px 0 0 0', color: C.text, fontSize: 11, overflow: 'auto', maxHeight: 120 }}>
                {JSON.stringify(entry.params, null, 2)}
              </pre>
            </div>
          )}
          {entry.sql && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ color: C.textMuted, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em' }}>SQL Query</span>
                <CopyButton text={entry.sql} label="Copy SQL" />
              </div>
              <pre style={{ background: '#0a0e14', border: `1px solid ${C.borderLight}`, borderRadius: 4, padding: 10, margin: '4px 0 0 0', color: '#e6edf3', fontSize: 11, overflow: 'auto', maxHeight: 300, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {entry.sql}
              </pre>
            </div>
          )}
          {entry.error && (
            <div style={{ marginBottom: 8 }}>
              <span style={{ color: C.red, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Error</span>
              <pre style={{ background: `${C.red}10`, border: `1px solid ${C.red}30`, borderRadius: 4, padding: 8, margin: '4px 0 0 0', color: C.red, fontSize: 11, overflow: 'auto', maxHeight: 200, whiteSpace: 'pre-wrap' }}>
                {entry.error}{entry.stackTrace && `\n\n${entry.stackTrace}`}
              </pre>
            </div>
          )}
          {!entry.sql && !entry.error && (
            <div style={{ color: C.textDim, fontSize: 11, fontStyle: 'italic' }}>
              No SQL captured — endpoint may not have debug instrumentation yet.
            </div>
          )}
          <div style={{ marginTop: 8 }}>
            <CopyButton text={clipboardText} label={'\uD83D\uDCCB Copy for Claude'} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Report Bug Modal ──

function ReportBugModal({ entries, onClose }: { entries: DebugEntry[]; onClose: () => void }) {
  const [description, setDescription] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const handleSubmit = async () => {
    if (!confirmed) return;
    setSending(true);
    try {
      await apiClient.post('/admin/bug-report', {
          kind: 'bug',
          description,
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          debugEntries: entries.slice(0, 50),
      });
      setSent(true);
      setTimeout(onClose, 2000);
    } catch (err) {
      alert('Failed to submit bug report.');
      console.error(err);
    } finally { setSending(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 100001, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: C.bgPanel, border: `1px solid ${C.border}`, borderRadius: 8, width: 500, maxWidth: '90vw', padding: 24 }} onClick={e => e.stopPropagation()}>
        {sent ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>{'\u2705'}</div>
            <div style={{ color: C.green, fontSize: 16 }}>Bug report submitted</div>
          </div>
        ) : (
          <>
            <h3 style={{ margin: '0 0 16px', color: C.text, fontFamily: C.sans, fontSize: 16 }}>Report Bug</h3>
            <div style={{ background: `${C.yellow}15`, border: `1px solid ${C.yellow}40`, borderRadius: 6, padding: 12, marginBottom: 16, fontSize: 12, color: C.yellow, lineHeight: 1.5 }}>
              {'\u26A0\uFE0F'} Debug data may contain confidential information including molecular structures in SMILES format. Do not submit if your current view contains proprietary compounds.
            </div>
            <textarea value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Describe the bug \u2014 what happened and what you expected..."
              style={{ width: '100%', minHeight: 100, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 4, padding: 10, color: C.text, fontFamily: C.sans, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
            />
            <div style={{ fontSize: 11, color: C.textDim, margin: '8px 0 12px' }}>
              Includes {entries.length} debug log {entries.length === 1 ? 'entry' : 'entries'} + current URL + browser info.
            </div>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 16, cursor: 'pointer', fontSize: 12, color: C.textMuted }}>
              <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} style={{ marginTop: 2 }} />
              I confirm this data does not contain proprietary compounds I shouldn't share internally.
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={{ background: 'transparent', border: `1px solid ${C.border}`, color: C.textMuted, padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={handleSubmit} disabled={!confirmed || !description.trim() || sending} style={{ background: confirmed && description.trim() ? C.accent : C.textDim, border: 'none', color: '#fff', padding: '8px 20px', borderRadius: 6, cursor: confirmed && description.trim() ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600, opacity: sending ? 0.6 : 1 }}>
                {sending ? 'Submitting...' : 'Submit Bug Report'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Main Debug Panel ──

export default function DebugPanel({ userRole }: DebugPanelProps) {
  const [, forceUpdate] = useState(0);
  const [panelOpen, setPanelOpen] = useState(false);
  const [showBugReport, setShowBugReport] = useState(false);
  const [panelWidth, setPanelWidth] = useState(520);
  const [filter, setFilter] = useState('');
  const isResizing = useRef(false);

  const isAdmin = userRole === 'admin' || userRole === 'super_admin';

  useEffect(() => {
    if (!isAdmin) return;
    installDebugInterceptor();
    return debugStore.subscribe(() => forceUpdate(n => n + 1));
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    const openPanel = () => {
      debugStore.activate();
      setPanelOpen(true);
    };
    window.addEventListener('adroit:open-debug-panel', openPanel);
    return () => window.removeEventListener('adroit:open-debug-panel', openPanel);
  }, [isAdmin]);

  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
    const startX = e.clientX;
    const startWidth = panelWidth;
    const onMouseMove = (ev: MouseEvent) => {
      if (!isResizing.current) return;
      setPanelWidth(Math.max(350, Math.min(900, startWidth + (startX - ev.clientX))));
    };
    const onMouseUp = () => { isResizing.current = false; document.removeEventListener('mousemove', onMouseMove); document.removeEventListener('mouseup', onMouseUp); };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [panelWidth]);

  if (!isAdmin) return null;

  const entries = debugStore.getEntries();
  const filteredEntries = filter
    ? entries.filter(e => e.endpoint.toLowerCase().includes(filter.toLowerCase()) || (e.sql || '').toLowerCase().includes(filter.toLowerCase()) || e.tables.some(t => t.toLowerCase().includes(filter.toLowerCase())))
    : entries;
  return (
    <>
      {/* Slide-out panel */}
      {panelOpen && (
        <div style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: panelWidth, zIndex: 100000,
          background: C.bg, borderLeft: `1px solid ${C.border}`,
          display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 24px rgba(0,0,0,0.5)', fontFamily: C.sans,
        }}>
          {/* Resize handle */}
          <div onMouseDown={startResize} style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, cursor: 'ew-resize', zIndex: 1 }}
            onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = C.accent}
            onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}
          />

          {/* Header */}
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: C.accent }}>{'\uD83D\uDD0D'} Debug Panel</span>
            <span style={{ fontSize: 10, color: C.green, background: `${C.green}15`, padding: '2px 8px', borderRadius: 10, fontWeight: 500 }}>CAPTURING</span>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 11, color: C.textDim }}>{entries.length} {entries.length === 1 ? 'entry' : 'entries'}</span>
            <button
              onClick={() => {
                const w = window.open(
                  '/popout/debug',
                  'debug-panel',
                  'width=600,height=800,menubar=no,toolbar=no,location=no,status=no,scrollbars=yes,resizable=yes'
                );
                if (w) w.focus();
              }}
              title="Pop out Debug Panel"
              style={{
                background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 4,
                color: C.textMuted, cursor: 'pointer', width: 28, height: 28,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13,
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = C.text; (e.currentTarget as HTMLElement).style.borderColor = C.accent; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = C.textMuted; (e.currentTarget as HTMLElement).style.borderColor = C.border; }}
            >
              {'\u2197'}
            </button>
            <button
              onClick={() => { setPanelOpen(false); debugStore.deactivate(); }}
              title="Close Debug Panel"
              style={{
                background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 4,
                color: C.textMuted, cursor: 'pointer', width: 28, height: 28,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = C.text; (e.currentTarget as HTMLElement).style.borderColor = C.accent; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = C.textMuted; (e.currentTarget as HTMLElement).style.borderColor = C.border; }}
            >
              {'\u2715'}
            </button>
          </div>

          {/* Toolbar */}
          <div style={{ padding: '8px 16px', borderBottom: `1px solid ${C.borderLight}`, display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <input type="text" value={filter} onChange={e => setFilter(e.target.value)}
              placeholder="Filter by endpoint, SQL, or table..."
              style={{ flex: 1, background: C.bgPanel, border: `1px solid ${C.borderLight}`, borderRadius: 4, padding: '5px 10px', color: C.text, fontSize: 12, fontFamily: C.mono }}
            />
            <button onClick={() => debugStore.clear()} style={{ background: 'transparent', border: `1px solid ${C.border}`, color: C.textMuted, padding: '4px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}>Clear</button>
            <button onClick={() => setShowBugReport(true)} style={{ background: `${C.red}15`, border: `1px solid ${C.red}40`, color: C.red, padding: '4px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>{'\uD83D\uDC1B'} Report Bug</button>
          </div>

          {/* Entries */}
          <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
            {filteredEntries.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: C.textDim, fontSize: 13 }}>
                {entries.length === 0 ? <>No API calls captured yet.<br /><span style={{ fontSize: 11 }}>Navigate around — queries will appear here.</span></> : 'No entries match your filter.'}
              </div>
            ) : filteredEntries.map(entry => <DebugEntryRow key={entry.id} entry={entry} />)}
          </div>

          {/* Footer */}
          <div style={{ padding: '8px 16px', borderTop: `1px solid ${C.borderLight}`, fontSize: 10, color: C.textDim, display: 'flex', justifyContent: 'space-between', flexShrink: 0 }}>
            <span>Admin debug — not visible to members</span>
            <span>Max 200 entries</span>
          </div>
        </div>
      )}

      {showBugReport && <ReportBugModal entries={entries} onClose={() => setShowBugReport(false)} />}
    </>
  );
}
